import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shared/lib/utils';

/**
 * Botão base (estilo shadcn/ui "new-york", escrito à mão — sem depender do
 * CLI, que precisa de rede). `class-variance-authority` já é dependência do
 * projeto; nenhuma tecnologia nova entra aqui (regra 13).
 */
/**
 * Exportado para o caso do LINK que precisa parecer botão ("Novo projeto"
 * abre uma rota, não dispara uma ação). A alternativa seria `asChild` com o
 * Slot do Radix — dependência nova (regra 13) para resolver um `className`.
 * Navegação continua sendo `<Link>`: clicar com o meio, abrir em outra aba e
 * ver o endereço na barra de status são coisas que um `<button>` com
 * `navigate()` perde em silêncio.
 */
/**
 * O `active:scale-[0.97]` é o único movimento aqui, e é o mais importante do
 * painel: ele responde no `pointerdown`, antes de qualquer rede. Num sistema em
 * que "Enviar para análise" fala com o Postgres, esse recuo de 3% é o que
 * separa "cliquei e o sistema recebeu" de "cliquei e não sei se pegou" — e é o
 * que evita o duplo clique de quem ficou na dúvida.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ' +
    'transition-[color,background-color,border-color,opacity,transform,box-shadow] duration-fast ease-out ' +
    'active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
    'focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100',
  {
    variants: {
      variant: {
        /**
         * A ação da tela. Uma por tela, quase sempre — é o azul da igreja, e
         * ele só funciona como "clique aqui" enquanto for raro.
         *
         * A sombra é tingida com o próprio azul (`hsl(var(--primary)/…)`) e
         * não com preto: sombra preta sob um botão colorido cinza o entorno e
         * faz o botão parecer sujo. Duas camadas porque uma só não é sombra —
         * a de 1px é o contato com a superfície, a de 6px é a distância até
         * ela. No hover a segunda cresce e o azul escurece: o botão sobe.
         */
        default:
          'bg-primary text-primary-foreground ' +
          'shadow-[0_1px_2px_hsl(var(--primary)/0.24),0_2px_6px_-2px_hsl(var(--primary)/0.30)] ' +
          'hover:bg-primary-hover ' +
          'hover:shadow-[0_1px_2px_hsl(var(--primary)/0.30),0_5px_14px_-3px_hsl(var(--primary)/0.38)] ' +
          'disabled:shadow-none',
        /**
         * `bg-transparent` e não `bg-background`: as duas superfícies do painel
         * estão trocadas (ver `AppShell`), então um botão pintado de
         * `--background` dentro da área de conteúdo vira um retângulo cinza
         * sobre papel branco — lê como campo desabilitado, não como botão.
         * Transparente, ele herda a superfície onde caiu e continua certo nas
         * duas.
         *
         * A borda esquenta para o azul no hover. É o único sinal de que este
         * botão também é da marca; sem ele, "Ver rascunhos" ao lado de "Novo
         * projeto" parece de outro sistema.
         */
        outline:
          'border border-input bg-transparent shadow-[0_1px_2px_0_rgb(0_0_0/0.04)] ' +
          'hover:border-ring/40 hover:bg-accent hover:text-accent-foreground disabled:shadow-none',
        /**
         * Ação que não volta atrás, e SÓ dentro da confirmação dela — nunca na
         * tela. Descartar rascunho aparece em cinza no cabeçalho e só vira
         * vermelho depois da pergunta: vermelho de repouso ao lado de um
         * formulário transforma a tela inteira num campo minado, e quem vê
         * perigo o tempo todo para de ver perigo.
         */
        destructive:
          'bg-destructive text-destructive-foreground ' +
          'shadow-[0_1px_2px_hsl(var(--destructive)/0.24),0_2px_6px_-2px_hsl(var(--destructive)/0.30)] ' +
          'hover:bg-destructive/90 ' +
          'hover:shadow-[0_1px_2px_hsl(var(--destructive)/0.30),0_5px_14px_-3px_hsl(var(--destructive)/0.38)] ' +
          'disabled:shadow-none',
        /**
         * O verbo que produz `approved` (decisão 41) — a cor do CRACHÁ que ele
         * causa, aplicada ao botão. Não é paleta nova: é a paleta da situação,
         * usada no lugar que a provoca.
         *
         * **`emerald-700`, e não `emerald-600`.** Com texto branco, o 600 dá
         * ~3,8:1 e reprova no AA para texto normal; o 700 dá ~5,5:1. Hover em
         * 800 — a mesma mecânica de `default`: a sombra é tingida com a própria
         * cor, nunca preta, senão o botão parece sujo.
         *
         * É o único verbo preenchido dos três. Três botões coloridos lado a lado
         * viram árvore de Natal e nenhum é primário; os dois destrutivos revelam
         * a cor no hover/foco, que é onde ela precisa confirmar "você está
         * prestes a fazer ISTO".
         */
        success:
          'bg-emerald-700 text-white ' +
          'shadow-[0_1px_2px_rgb(4_120_87/0.24),0_2px_6px_-2px_rgb(4_120_87/0.30)] ' +
          'hover:bg-emerald-800 ' +
          'hover:shadow-[0_1px_2px_rgb(4_120_87/0.30),0_5px_14px_-3px_rgb(4_120_87/0.38)] ' +
          'disabled:shadow-none',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        /**
         * Texto e mais nada — sem fundo, sem borda, sublinhado só no hover.
         *
         * É a forma do verbo REPETIDO: um "Analisar" por linha na fila
         * `/analise`. Vinte botões preenchidos numa coluna não dão vinte ações
         * primárias, dão uma faixa de cor que o olho aprende a ignorar; vinte
         * palavras azuis alinhadas à direita dizem onde clicar sem disputar
         * com o título ao lado.
         *
         * A cor é `--primary`, e não `--accent`: este último é a superfície de
         * hover (95% de luminosidade) e, aplicado a TEXTO, some no branco da
         * tabela — ~1,1:1, longe do 4,5:1 do AA.
         */
        link: 'text-primary underline-offset-4 hover:text-primary-hover hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
